import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

/**
 * Premium ambient hero glow — a layered radial gradient with a slow,
 * breathing animation that sits behind hero content. Feels alive
 * without being distracting.
 */
export function HeroGlow({ className }: { className?: string }) {
  const [active, setActive] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setActive(true), 200);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      aria-hidden
      className={cn(
        "pointer-events-none absolute inset-0 overflow-hidden",
        className
      )}
    >
      {/* Primary accent orb */}
      <div
        className={cn(
          "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-[40%] rounded-full",
          "w-[600px] h-[400px] sm:w-[800px] sm:h-[500px] lg:w-[1000px] lg:h-[600px]",
          "bg-gradient-radial from-accent/20 via-accent/5 to-transparent",
          "blur-3xl transition-all duration-[2000ms] ease-out",
          active ? "opacity-100 scale-100" : "opacity-0 scale-75"
        )}
      />

      {/* Secondary primary orb — offset for depth */}
      <div
        className={cn(
          "absolute left-[30%] top-[60%] -translate-x-1/2 -translate-y-1/2 rounded-full",
          "w-[400px] h-[300px] sm:w-[500px] sm:h-[350px]",
          "bg-gradient-radial from-primary/15 via-primary/5 to-transparent",
          "blur-3xl transition-all duration-[2500ms] ease-out delay-300",
          active ? "opacity-100 scale-100" : "opacity-0 scale-50"
        )}
      />

      {/* Subtle animated pulse ring */}
      <div
        className={cn(
          "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-[35%] rounded-full",
          "w-[300px] h-[300px] sm:w-[400px] sm:h-[400px]",
          "border border-accent/10",
          "transition-all duration-[2000ms] ease-out delay-500",
          active ? "opacity-100 scale-100 animate-pulse" : "opacity-0 scale-50"
        )}
      />
    </div>
  );
}
